import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Form, Input, InputNumber, Radio, Alert, Steps, Button, Space, Typography, Tag, theme } from 'antd';
import apiClient from '../api/client';

const { Text, Paragraph } = Typography;

// Installing a checker onto a server, from the panel.
//
// The credential is the whole reason this component exists as its own file: it
// lives in React state, goes out in one request body, and is wiped when the
// modal closes — including after a success, which is why the reset hangs off
// afterClose rather than off cancel. The browser keeps none of it; the gateway
// keeps it encrypted if the host accepted it, which is what the "Saved" hint on
// the field means.

export interface WorkerInstallTarget {
  id: string;
  name: string;
  url: string;
  installed?: boolean;
}

interface Preflight {
  host: string;
  checkerPort: number;
  workerUrl: string;
  runningPort?: number;
  runningName?: string;
  plannedName?: string;
  // Whether a credential is remembered for this host, and which kind. Never
  // the credential itself — there is no field in this dialog that could show
  // it, and the API does not return one.
  savedCredential?: boolean;
  savedKind?: string;
  sshUser: string;
  sshPort: number;
  artifactAvailable: boolean;
  artifactError?: string;
  artifactSha256?: string;
  install?: { status?: string; hostKeyFingerprint?: string; binarySha256?: string; at?: string };
}

interface JobLine { seq: number; at: string; stage: string; level: string; text: string }
interface Job {
  id: string; state: string; stage: string; action: string;
  cause?: string; message?: string; fingerprint?: string;
}

// The stages the backend reports, in order, with what they mean to a human.
const STAGES: Array<{ key: string; title: string }> = [
  { key: 'connect', title: 'SSH connection' },
  { key: 'probe', title: 'Inspecting the host' },
  { key: 'upload', title: 'Uploading the checker' },
  { key: 'config', title: 'Configuration' },
  { key: 'unit', title: 'systemd service' },
  { key: 'activate', title: 'Start and enable at boot' },
  { key: 'verify', title: 'Answering on the host' },
  { key: 'reach', title: 'Reachable from the panel' },
];

// What to tell the operator for each failure. Without this the panel would
// print an SSH error and leave them to guess.
const REMEDIES: Record<string, string> = {
  unreachable: 'No SSH answer from the host. Check the address and port, and that the server is up.',
  auth_rejected: 'The host rejected these credentials. Check them — nothing is retried automatically, to stay clear of brute-force lockouts.',
  host_key: 'The host key is not confirmed — the fingerprint is above.',
  not_root: 'This account is not root and has no passwordless sudo. The install needs administrator rights.',
  no_systemd: 'This host has no systemd. The checker is installed as a systemd service — a different system image is needed.',
  wrong_arch: 'Processor architecture does not match: the bundled checker is built for x86_64.',
  disk_full: 'Not enough free space on the host for the checker binary.',
  unhealthy: 'The checker is installed but does not answer on the host itself. Its last log lines are below.',
  no_artifact: 'This panel build carries no checker binary. The gateway image has to be rebuilt.',
  key_unusable: 'This private key could not be read here — nothing was sent to the host. Check the format, or enter its passphrase.',
  unknown: 'See the output below — it shows where this stopped.',
};

const WorkerInstallModal: React.FC<{
  target: WorkerInstallTarget | null;
  action: 'install' | 'uninstall';
  onClose: () => void;
  onFinished: () => void;
}> = ({ target, action, onClose, onFinished }) => {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [preflight, setPreflight] = useState<Preflight | null>(null);
  const [authMode, setAuthMode] = useState<'password' | 'key'>('password');
  const [job, setJob] = useState<Job | null>(null);
  const [lines, setLines] = useState<JobLine[]>([]);
  const [busy, setBusy] = useState(false);
  const [fingerprint, setFingerprint] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const seqRef = useRef(0);
  // What the operator typed. Kept here because the host-key question arrives
  // when the form is no longer on screen — see start().
  const credsRef = useRef<Record<string, unknown> | null>(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => {
    if (!target) return;
    setPreflight(null);
    apiClient.get(`/admin/workers/${target.id}/provision`)
      .then(({ data }) => {
        setPreflight(data.data);
        form.setFieldsValue({
          sshUser: data.data.sshUser,
          sshPort: data.data.sshPort,
          workerUrl: data.data.workerUrl,
          checkerPort: data.data.checkerPort,
        });
        // Open on the credential that is actually saved, or the placeholder
        // sits under a field the operator is not looking at.
        if (data.data.savedKind === 'key') setAuthMode('key');
      })
      .catch((e) => setError(e.response?.data?.error || 'Could not load this host\'s details'));
    return stopPolling;
  }, [target, form]);

  const poll = useCallback((workerId: string, jobId: string) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await apiClient.get(
          `/admin/workers/${workerId}/provision/${jobId}?since=${seqRef.current}`);
        const payload = data.data;
        if (payload.lines?.length) {
          seqRef.current = payload.nextSeq;
          setLines((prev) => [...prev, ...payload.lines]);
        }
        setJob(payload.job);
        if (payload.job.state !== 'running') {
          stopPolling();
          setBusy(false);
          if (payload.job.state === 'succeeded') onFinished();
          if (payload.job.cause === 'host_key') setFingerprint(payload.job.fingerprint || null);
        }
      } catch (e) {
        stopPolling();
        setBusy(false);
      }
    }, 1500);
  }, [onFinished]);

  const start = async (acceptHostKey: boolean) => {
    if (!target) return;

    // The first attempt reads the form. Confirming the host key does not: by
    // then the job is on screen, the form is unmounted, and validateFields() on
    // an unconnected instance resolves with an empty object. The retry used to
    // go out without a password and the API answered "a password or a private
    // key is required" — so the button failed in exactly the case it exists
    // for, since a first install always begins with the host-key question.
    let creds = credsRef.current;
    if (!job) {
      const values = await form.validateFields().catch(() => null);
      if (!values) return;
      creds = {
        sshUser: values.sshUser,
        sshPort: values.sshPort,
        // The address is both what the panel dials and the host this installs
        // onto, so an edit here has to travel with the job.
        workerUrl: values.workerUrl,
        checkerPort: values.checkerPort,
        sshPassword: authMode === 'password' ? values.sshPassword : undefined,
        sshPrivateKey: authMode === 'key' ? values.sshPrivateKey : undefined,
        sshPassphrase: authMode === 'key' ? values.sshPassphrase : undefined,
      };
      credsRef.current = creds;
    }
    if (!creds) return;

    setBusy(true);
    setError(null);
    setLines([]);
    setJob(null);
    seqRef.current = 0;
    try {
      const { data } = await apiClient.post(`/admin/workers/${target.id}/provision`, {
        action,
        ...creds,
        acceptHostKey,
        acceptedFingerprint: acceptHostKey ? (job?.fingerprint || fingerprint || undefined) : undefined,
      });
      poll(target.id, data.data.jobId);
    } catch (e: any) {
      setBusy(false);
      setError(e.response?.data?.error || 'Could not start the job');
    }
  };

  // Watched rather than read once: the warning has to follow what is being
  // typed, not what the dialog opened with.
  const typedUrl: string = Form.useWatch('workerUrl', form) ?? '';
  const checkerPort: number | undefined = Form.useWatch('checkerPort', form);
  const urlPort = (() => {
    const m = typedUrl.match(/^[a-z]+:\/\/[^/]*?:(\d+)/i);
    if (m) return Number(m[1]);
    if (/^https:\/\//i.test(typedUrl)) return 443;
    if (/^http:\/\//i.test(typedUrl)) return 80;
    return undefined;
  })();
  const portMismatch = !!(urlPort && checkerPort && urlPort !== checkerPort);

  // A saved credential only prefills the field it was saved as.
  const savedFor = (kind: 'password' | 'key') =>
    !!preflight?.savedCredential && (preflight?.savedKind || 'password') === kind;

  const forget = async () => {
    if (!target) return;
    try {
      await apiClient.delete(`/admin/workers/${target.id}/credentials`);
      setPreflight((p) => (p ? { ...p, savedCredential: false, savedKind: '' } : p));
      form.setFieldsValue({ sshPassword: undefined, sshPrivateKey: undefined });
    } catch (e: any) {
      setError(e.response?.data?.error || 'Could not forget the saved credential');
    }
  };

  const forgetLink = (
    <Button type="link" size="small" style={{ padding: 0 }} onClick={forget}>
      Forget the saved credential
    </Button>
  );

  const currentStage = Math.max(0, STAGES.findIndex((s) => s.key === job?.stage));
  const failed = job?.state === 'failed';
  const succeeded = job?.state === 'succeeded';
  const askingHostKey = failed && job?.cause === 'host_key';

  return (
    <Modal
      open={!!target}
      title={action === 'install'
        ? `Install the checker on ${preflight?.host || target?.url || ''}`
        : `Remove the checker from ${preflight?.host || ''}`}
      onCancel={() => { stopPolling(); onClose(); }}
      afterClose={() => {
        // Wipe the credentials — out of the form and out of the dialog's own
        // memory, success included.
        form.resetFields();
        credsRef.current = null;
        setJob(null); setLines([]); setFingerprint(null); setError(null); setBusy(false);
      }}
      width={720}
      footer={null}
      destroyOnClose
    >
      {preflight && !preflight.artifactAvailable && action === 'install' && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }}
          message="This panel build carries no checker binary"
          description={<>The gateway image has to be rebuilt — an install ships exactly the checker this panel was built with.
            <br /><Text type="secondary">{preflight.artifactError}</Text></>} />
      )}

      {!job && (
        <Form form={form} layout="vertical" size="small" initialValues={{ sshUser: 'root', sshPort: 22 }}>
          {action === 'install' && (
            <>
              <Space size={16} align="start" style={{ display: 'flex' }}>
                <Form.Item name="workerUrl" label="Connection URL" rules={[{ required: true }]} style={{ flex: 1, marginBottom: 12 }}
                  extra="Where the panel will dial this worker — and the host it installs onto. Editing it saves the new address on the worker.">
                  <Input placeholder="http://10.0.0.5:8081" autoComplete="off" />
                </Form.Item>
                <Form.Item name="checkerPort" label="Checker port" rules={[{ required: true }]} style={{ width: 140, marginBottom: 12 }}
                  extra={preflight?.runningPort && preflight.runningPort !== preflight.checkerPort
                    ? `What the service binds. It reports ${preflight.runningPort} right now.`
                    : 'What the service binds.'}>
                  <InputNumber min={1} max={65535} style={{ width: '100%' }} />
                </Form.Item>
              </Space>
              {preflight?.runningName && preflight?.plannedName
                && preflight.runningName !== preflight.plannedName && (
                <Alert type="warning" showIcon style={{ marginBottom: 16 }}
                  message={`This will rename the checker from "${preflight.runningName}" to "${preflight.plannedName}"`}
                  description="The checker reports that name with every result, so checks made before and after will be attributed to different names in logs and reports. It comes from this worker's name in the panel — rename the worker first if you want to keep the old one." />
              )}
              {portMismatch && (
                <Alert type="warning" showIcon style={{ marginBottom: 16 }}
                  message={`The panel would dial port ${urlPort} while the checker listens on ${checkerPort}`}
                  description="That only works if something forwards one to the other on this host. Otherwise make them the same." />
              )}
            </>
          )}
          <Space size={16} style={{ display: 'flex' }}>
            <Form.Item name="sshUser" label="SSH user" rules={[{ required: true }]} style={{ flex: 1, marginBottom: 12 }}>
              <Input placeholder="root" autoComplete="off" />
            </Form.Item>
            <Form.Item name="sshPort" label="SSH port" rules={[{ required: true }]} style={{ width: 120, marginBottom: 12 }}>
              <InputNumber min={1} max={65535} style={{ width: '100%' }} />
            </Form.Item>
          </Space>

          <Radio.Group size="small" value={authMode} onChange={(e) => setAuthMode(e.target.value)} style={{ marginBottom: 12 }}>
            <Radio.Button value="password">Password</Radio.Button>
            <Radio.Button value="key">Private key</Radio.Button>
          </Radio.Group>

          {authMode === 'password' ? (
            <Form.Item name="sshPassword" label="Password" style={{ marginBottom: 12 }}
              rules={[{ required: !savedFor('password'), message: 'Enter the password' }]}
              extra={savedFor('password') ? forgetLink : undefined}>
              <Input.Password autoComplete="new-password"
                placeholder={savedFor('password') ? 'Saved — leave empty to reuse it' : undefined} />
            </Form.Item>
          ) : (
            <>
              <Form.Item name="sshPrivateKey" label="Private key" style={{ marginBottom: 12 }}
                rules={[{ required: !savedFor('key'), message: 'Paste the key' }]}
                extra={savedFor('key') ? forgetLink : undefined}>
                <Input.TextArea rows={4} style={{ fontFamily: 'monospace', fontSize: 12 }}
                  placeholder={savedFor('key') ? 'Saved — leave empty to reuse it' : '-----BEGIN OPENSSH PRIVATE KEY-----'} />
              </Form.Item>
              <Form.Item name="sshPassphrase" label="Key passphrase (if any)" style={{ marginBottom: 12 }}>
                <Input.Password autoComplete="new-password" />
              </Form.Item>
            </>
          )}

          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message={preflight?.savedCredential
              ? 'This host has a saved credential'
              : 'What you type here is remembered for this host'}
            description={preflight?.savedCredential
              ? 'Leave the field empty to reuse it, or type a new one to replace it. It is stored encrypted and is never shown — not here, not anywhere else in the panel.'
              : 'Once the host accepts it, it is kept encrypted so the next install does not have to ask. It is never shown again, and never appears in a log.'} />

          <Button type="primary" loading={busy} onClick={() => start(false)}
            disabled={action === 'install' && preflight ? !preflight.artifactAvailable : false}>
            {action === 'install' ? 'Install' : 'Remove the checker'}
          </Button>
        </Form>
      )}

      {job && (
        <>
          <Steps
            direction="vertical" size="small" current={currentStage}
            status={failed ? 'error' : succeeded ? 'finish' : 'process'}
            items={STAGES.map((s) => ({
              title: s.title,
              description: lines.filter((l) => l.stage === s.key).map((l) => l.text).slice(-2).join(' · ') || undefined,
            }))}
            style={{ marginBottom: 16 }}
          />

          {succeeded && (
            <Alert type="success" showIcon
              message={action === 'install'
                ? 'Done — the checker is installed, running and enabled at boot'
                : 'The checker has been removed from the host'}
              description={action === 'install'
                ? 'The panel is connecting to it right now. The worker entry stays as it is.'
                : 'The worker entry is still in the list — delete it separately if it is no longer needed.'} />
          )}

          {askingHostKey && (
            <Alert type="warning" showIcon
              message={preflight?.install?.hostKeyFingerprint ? 'The host key has changed' : 'Confirm this host'}
              description={
                <>
                  <Paragraph style={{ marginBottom: 8 }}>
                    Key fingerprint: <Text code>{job.fingerprint || fingerprint}</Text>
                  </Paragraph>
                  <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                    {preflight?.install?.hostKeyFingerprint
                      ? 'This address used to present a different key. That happens after a server is rebuilt — and it also looks exactly like an impostor. Nothing has been sent and nothing has been changed.'
                      : 'Compare it with what your hosting panel shows. The password has not been sent to the host yet.'}
                  </Paragraph>
                  <Button type="primary" danger={!!preflight?.install?.hostKeyFingerprint}
                    loading={busy} onClick={() => start(true)}>
                    Trust this host and continue
                  </Button>
                </>
              } />
          )}

          {failed && !askingHostKey && (
            <Alert type="error" showIcon
              message={`Failed at: ${STAGES.find((s) => s.key === job.stage)?.title || job.stage}`}
              description={
                <>
                  <Paragraph style={{ marginBottom: 8 }}>{REMEDIES[job.cause || 'unknown']}</Paragraph>
                  {job.message && <Text type="secondary" style={{ fontSize: 12 }}>{job.message}</Text>}
                </>
              } />
          )}

          {lines.length > 0 && (
            <pre style={{
              marginTop: 12, maxHeight: 200, overflow: 'auto', fontSize: 12,
              background: token.colorFillQuaternary,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadius, padding: 8,
            }}>
              {lines.map((l) => (
                <div key={l.seq} style={{ color: l.level === 'error' ? token.colorError : l.level === 'warn' ? token.colorWarning : token.colorTextSecondary }}>
                  <Tag style={{ fontSize: 12, lineHeight: '16px', padding: '0 4px', marginRight: 6 }}>{l.stage}</Tag>
                  {l.text}
                </div>
              ))}
            </pre>
          )}

          {(succeeded || (failed && !askingHostKey)) && (
            <Space style={{ marginTop: 16 }}>
              <Button onClick={() => { stopPolling(); onClose(); }}>Close</Button>
              {failed && <Button onClick={() => { setJob(null); setLines([]); }}>Change the details and retry</Button>}
            </Space>
          )}
        </>
      )}

      {error && <Alert type="error" showIcon style={{ marginTop: 12 }} message={error} />}
    </Modal>
  );
};

export default WorkerInstallModal;
